import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, ShieldCheck } from 'lucide-react';

/**
 * AprTrustBlock — "Certificate of Analysis" style trust surface for APR CTAs.
 *
 * Variants:
 *   - "full"    : editorial lab-report block (use once per peptide article)
 *   - "compact" : single horizontal data bar (use directly above every APR CTA)
 *
 * Props:
 *   - variant   : 'full' | 'compact'  (default 'full')
 *   - href      : APR affiliate URL (defaults to APR home /ref/16/)
 *   - cta       : button label
 *   - peptide   : optional SKU tag shown in compact variant (e.g. "BPC-157 · 10 mg")
 */

const APR_HOME = 'https://www.americanpeptideresearch.net/ref/16/';
const MONO = { fontFamily: '"JetBrains Mono", ui-monospace, monospace' };
const DISPLAY = { fontFamily: '"Bebas Neue", sans-serif' };

const METRICS = [
  { k: 'PURITY',     v: '99%+',         sub: 'HPLC-verified' },
  { k: 'TESTING',    v: 'THIRD-PARTY',  sub: 'independent lab' },
  { k: 'COA',        v: 'PER BATCH',    sub: 'available on request' },
  { k: 'SHIPPING',   v: 'USA',          sub: 'cold-chain packed' },
];

function useInView(ref, options = { threshold: 0.25 }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setVisible(true);
        obs.disconnect();
      }
    }, options);
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, options]);
  return visible;
}

export function AprTrustBlock({ variant = 'full', href = APR_HOME, cta = 'Shop APR', peptide }) {
  const ref = useRef(null);
  const visible = useInView(ref);

  if (variant === 'compact') {
    return (
      <div
        ref={ref}
        data-testid="apr-trust-block-compact"
        className="relative w-full overflow-hidden"
        style={{
          backgroundColor: '#0a0a0a',
          borderTop: '1px solid rgba(200,255,0,0.22)',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(6px)',
          transition: 'opacity 450ms ease, transform 450ms ease',
        }}
      >
        {/* Scan line */}
        <span
          aria-hidden
          className="pointer-events-none absolute top-0 left-0 h-px"
          style={{
            width: '30%',
            background: 'linear-gradient(90deg, transparent, #c8ff00, transparent)',
            animation: visible ? 'apr-scan 3.2s linear infinite' : 'none',
          }}
        />
        <div className="relative flex items-center gap-3 sm:gap-5 px-4 py-2.5 flex-wrap">
          {/* Verified dot */}
          <div className="flex items-center gap-2">
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-[#c8ff00] opacity-75 animate-ping" />
              <span className="relative rounded-full w-2 h-2 bg-[#c8ff00]" />
            </span>
            <span style={MONO} className="text-[10px] tracking-[0.25em] text-[#c8ff00] uppercase">Verified</span>
          </div>
          <span aria-hidden className="text-zinc-800">│</span>
          {/* Metrics — inline */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {METRICS.map((m) => (
              <div key={m.k} className="flex items-baseline gap-1.5">
                <span style={MONO} className="text-[9px] tracking-[0.2em] text-zinc-500 uppercase">{m.k}</span>
                <span style={MONO} className="text-[11px] text-white font-medium">{m.v}</span>
              </div>
            ))}
          </div>
          <div className="flex-1" />
          {peptide && (
            <span style={MONO} className="hidden md:inline text-[10px] tracking-[0.2em] text-zinc-500 uppercase">
              LOT · {peptide}
            </span>
          )}
        </div>
        <style>{`@keyframes apr-scan { 0% { left: -30%; } 100% { left: 130%; } }`}</style>
      </div>
    );
  }

  // ─── FULL LAB-REPORT VARIANT ──────────────────────────────────────────
  const timestamp = new Date().toISOString().slice(0, 10);

  return (
    <aside
      ref={ref}
      data-testid="apr-trust-block"
      className="relative my-10"
      style={{
        backgroundColor: '#0a0a0a',
        color: '#e5e7eb',
        // grainy grid texture via data URI
        backgroundImage:
          'radial-gradient(rgba(255,255,255,0.035) 1px, transparent 1px)',
        backgroundSize: '18px 18px',
        backgroundPosition: '0 0',
      }}
    >
      {/* Corner brackets */}
      {[
        ['tl', 'top-0 left-0 border-t-2 border-l-2'],
        ['tr', 'top-0 right-0 border-t-2 border-r-2'],
        ['bl', 'bottom-0 left-0 border-b-2 border-l-2'],
        ['br', 'bottom-0 right-0 border-b-2 border-r-2'],
      ].map(([pos, posClasses]) => (
        <span
          key={pos}
          aria-hidden
          className={`absolute w-4 h-4 border-[#c8ff00] ${posClasses}`}
        />
      ))}

      {/* Scanning line */}
      <span
        aria-hidden
        className="pointer-events-none absolute top-0 h-[2px]"
        style={{
          width: '40%',
          background: 'linear-gradient(90deg, transparent, rgba(200,255,0,0.65), transparent)',
          animation: visible ? 'apr-scan-full 4.5s linear infinite' : 'none',
        }}
      />

      {/* Top meta strip */}
      <div className="flex items-stretch border-b border-zinc-900">
        <div className="flex items-center gap-2.5 px-5 py-2.5 border-r border-zinc-900">
          <ShieldCheck className="w-3.5 h-3.5 text-[#c8ff00]" strokeWidth={2.25} />
          <span style={MONO} className="text-[10px] tracking-[0.3em] text-[#c8ff00] uppercase">
            APR · Certificate
          </span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2.5 border-r border-zinc-900">
          <span style={MONO} className="text-[10px] tracking-[0.2em] text-zinc-600 uppercase">Ref</span>
          <span style={MONO} className="text-[10px] tracking-[0.2em] text-zinc-300">/ref/16/</span>
        </div>
        <div className="hidden sm:flex items-center gap-2 px-4 py-2.5 border-r border-zinc-900">
          <span style={MONO} className="text-[10px] tracking-[0.2em] text-zinc-600 uppercase">Issued</span>
          <span style={MONO} className="text-[10px] tracking-[0.2em] text-zinc-300">{timestamp}</span>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2 px-4 py-2.5">
          <span className="relative flex w-1.5 h-1.5">
            <span className="absolute inset-0 rounded-full bg-[#c8ff00] opacity-75 animate-ping" />
            <span className="relative rounded-full w-1.5 h-1.5 bg-[#c8ff00]" />
          </span>
          <span style={MONO} className="text-[10px] tracking-[0.3em] text-[#c8ff00] uppercase">Verified</span>
        </div>
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Left column: headline */}
        <div className="lg:col-span-5 px-5 sm:px-8 py-7 border-b lg:border-b-0 lg:border-r border-zinc-900">
          <span style={MONO} className="block text-[10px] tracking-[0.35em] text-[#E63946] uppercase mb-4">
            ▸ Why American Peptide Research
          </span>
          <h3
            style={DISPLAY}
            className="text-3xl sm:text-4xl leading-[0.95] text-white mb-4"
          >
            Research-grade<br />
            or <span className="text-[#c8ff00]">don&apos;t bother.</span>
          </h3>
          <p className="text-sm text-zinc-400 leading-relaxed max-w-[44ch]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            NinjAthlete only partners with labs that publish a Certificate of Analysis for every batch.
            APR independently HPLC-tests each vial, ships cold-chain from the United States, and supplies
            a CoA on request — the exact standard we require for our YMYL peptide content.
          </p>
        </div>

        {/* Right column: metrics grid */}
        <div className="lg:col-span-7 relative">
          <div className="grid grid-cols-2 divide-x divide-y divide-zinc-900 border-b lg:border-b-0 border-zinc-900">
            {METRICS.map((m, i) => (
              <div
                key={m.k}
                className="px-5 sm:px-6 py-5"
                style={{
                  animation: visible ? `apr-rise 600ms ease both` : 'none',
                  animationDelay: `${150 + i * 100}ms`,
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span style={MONO} className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
                    {String(i + 1).padStart(2, '0')} · {m.k}
                  </span>
                </div>
                <div style={DISPLAY} className="text-2xl sm:text-3xl leading-none text-white mb-1.5">
                  {m.v}
                </div>
                <div style={MONO} className="text-[10px] tracking-[0.15em] text-zinc-500 uppercase">
                  {m.sub}
                </div>
              </div>
            ))}
          </div>

          {/* CTA strip */}
          <div className="flex items-stretch border-t border-zinc-900">
            <div className="flex-1 flex items-center gap-3 px-5 sm:px-6 py-4">
              <span style={MONO} className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase">Code</span>
              <span
                style={MONO}
                className="px-2 py-1 bg-[#c8ff00]/10 border border-[#c8ff00]/30 text-[#c8ff00] text-[11px] tracking-[0.2em]"
              >
                NINJA
              </span>
              <span style={MONO} className="text-[10px] tracking-[0.2em] text-zinc-600 uppercase hidden sm:inline">
                · 25% off
              </span>
            </div>
            <a
              href={href}
              target="_blank"
              rel="nofollow sponsored noopener noreferrer"
              data-testid="apr-trust-cta"
              className="group flex items-center gap-2.5 px-5 sm:px-7 bg-[#c8ff00] text-black hover:bg-white transition-colors"
              style={MONO}
            >
              <span className="text-[11px] font-bold tracking-[0.25em] uppercase">{cta}</span>
              <ExternalLink className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2.25} />
            </a>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes apr-scan-full { 0% { left: -40%; } 100% { left: 140%; } }
        @keyframes apr-rise {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </aside>
  );
}

export default AprTrustBlock;
